import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const inputCls =
  "w-full bg-storm-800/70 border border-storm-600 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-gold focus:border-gold/50 transition";

export default function Contact({ content }) {
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);
  const waText = encodeURIComponent("Hello DRC502 Construction, I would like to request a free quote.");

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", form);
      toast.success("Request received! We'll call you back shortly.");
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 sm:py-32" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-2 gap-14">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold mb-4">Free estimate</p>
            <h2 className="font-display font-bold italic uppercase text-4xl sm:text-5xl lg:text-6xl text-white leading-[0.95]" data-testid="contact-heading">
              Get your <span className="gold-text">free inspection.</span>
            </h2>
            <p className="mt-5 text-slate-400 text-base sm:text-lg leading-relaxed max-w-md">
              Tell us what's going on with your roof, siding or gutters. We respond the same day — emergencies immediately.
            </p>
            <div className="mt-10 flex flex-col gap-4">
              <a href={`tel:${content.phone_tel}`} data-testid="contact-call-link"
                className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-storm transition-colors">
                  <Phone size={18} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Call or text</span>
                  <span className="block text-white font-semibold group-hover:text-gold transition-colors">{content.phone_display}</span>
                </span>
              </a>
              <a href={`https://wa.me/${content.whatsapp}?text=${waText}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link"
                className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-storm transition-colors">
                  <MessageCircle size={18} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">WhatsApp</span>
                  <span className="block text-white font-semibold group-hover:text-gold transition-colors">Chat with us now</span>
                </span>
              </a>
              <a href={`mailto:${content.email}`} data-testid="contact-email-link"
                className="flex items-center gap-4 group">
                <span className="w-11 h-11 rounded-full border border-gold/30 flex items-center justify-center text-gold group-hover:bg-gold group-hover:text-storm transition-colors">
                  <Mail size={18} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email</span>
                  <span className="block text-white font-semibold group-hover:text-gold transition-colors">{content.email}</span>
                </span>
              </a>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative rounded-3xl border border-gold/20 bg-storm-800/50 backdrop-blur p-7 sm:p-9"
            data-testid="quote-form"
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2" htmlFor="q-name">Name *</label>
                <input id="q-name" required data-testid="quote-name-input" className={inputCls} placeholder="John Smith"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2" htmlFor="q-phone">Phone *</label>
                <input id="q-phone" required data-testid="quote-phone-input" className={inputCls} placeholder="(609) 555-0100"
                  value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="mt-4">
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2" htmlFor="q-email">Email</label>
              <input id="q-email" type="email" data-testid="quote-email-input" className={inputCls} placeholder="you@email.com"
                value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="mt-4">
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2" htmlFor="q-service">Service needed</label>
              <select id="q-service" data-testid="quote-service-select" className={inputCls}
                value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
                <option value="">Select a service…</option>
                {(content.services || []).map((s) => (
                  <option key={s.title} value={s.title}>{s.title}</option>
                ))}
                <option value="Not sure — need an inspection">Not sure — need an inspection</option>
              </select>
            </div>
            <div className="mt-4">
              <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2" htmlFor="q-message">Tell us more</label>
              <textarea id="q-message" rows={4} data-testid="quote-message-input" className={inputCls}
                placeholder="Leak over the garage, missing shingles after last night's storm…"
                value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
            </div>
            <button type="submit" disabled={sending} data-testid="quote-submit-button"
              className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-gold text-storm font-bold px-7 py-4 rounded-full hover:bg-gold-light transition-colors disabled:opacity-60">
              {sending ? "Sending…" : "Request My Free Inspection"}
              <Send size={16} />
            </button>
            <p className="mt-4 text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
              Same-day response · No obligation
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
